import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Info, RefreshCwIcon } from "lucide-react";
import { subHours } from "date-fns";
import { InstallationData } from "@/installation/installation";
import { useMultiplePeriodAnalysis } from "@/onPeriodAnalysis/api";
import { PeriodAnalysisData, SeriesId } from "@/onPeriodAnalysis/types";

type HealthSnapshotPeriod = 'last_24_hours' | 'last_7_days';

const HEALTH_SNAPSHOT_PERIODS = {
  LAST_24_HOURS: 'last_24_hours' as const,
  LAST_7_DAYS: 'last_7_days' as const,
} as const;

const HEALTH_SNAPSHOT_PERIOD_CONFIG: Record<HealthSnapshotPeriod, { hours: number; display: string; label: string }> = {
  last_24_hours: { hours: 24, display: 'the last 24 hours', label: 'Last 24 hours' },
  last_7_days: { hours: 7 * 24, display: 'the last 7 days', label: 'Last 7 days' },
};

const formatUnit = (unit: string | undefined): string | undefined => {
  if (!unit) return unit;

  const lowerUnit = unit.toLowerCase();

  if (['degf', 'degree_fahrenheit', 'fahrenheit', 'degreef'].includes(lowerUnit)) {
    return '\u00B0F';
  }

  if (['degc', 'degree_celsius', 'celsius', 'degreec'].includes(lowerUnit)) {
    return '\u00B0C';
  }

  return unit;
};

const calculateDeviation = (
  measured: number,
  min: number,
  max: number,
  type: 'inlet' | 'outlet',
  refrigerantType?: string
): { message: string; percentage: number } | null => {
  const rangeWidth = max - min;
  if (rangeWidth === 0) return null;

  const refrigerantSuffix = refrigerantType ? ` for ${refrigerantType}` : '';

  if (type === 'inlet' && measured < min) {
    const deviation = ((min - measured) / rangeWidth) * 100;
    return {
      message: `${Math.round(deviation)}% below expected${refrigerantSuffix}`,
      percentage: deviation
    };
  }

  if (type === 'outlet' && measured > max) {
    const deviation = ((measured - max) / rangeWidth) * 100;
    return {
      message: `${Math.round(deviation)}% above expected${refrigerantSuffix}`,
      percentage: deviation
    };
  }

  return null;
};

const DEVIATION_COLOR = '#eab308';

const PeriodAnalysisSnapshotCard = ({
  title,
  datum,
  suffix,
  isLoading,
  expectedRange,
  deviationMessage,
  accentColor,
}: {
  title: string;
  datum: number | undefined;
  suffix?: string;
  isLoading: boolean;
  expectedRange?: { min: number; max: number; unit: string };
  deviationMessage?: string;
  accentColor: string;
}) => {
  const hasData = datum !== undefined;
  const isRefetching = isLoading && hasData;
  const isInitialLoading = isLoading && !hasData;
  const hasDeviation = !!deviationMessage && hasData;
  const activeColor = hasDeviation ? DEVIATION_COLOR : accentColor;

  return (
    <div className="flex-1 min-w-0">
      <div
        className="relative bg-white rounded-lg overflow-hidden h-full flex flex-col transition-shadow duration-200 hover:shadow-lg border border-gray-100"
        style={{
          boxShadow: '0 1px 4px rgba(0,15,137,0.08), 0 4px 12px rgba(0,15,137,0.04)',
        }}
      >
        {isRefetching && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-50"
               style={{
                 background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
                 backgroundSize: '200% 100%',
                 animation: 'shimmer 5s infinite',
               }}
          />
        )}
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>

        {hasDeviation && (
          <div
            className="px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold"
            style={{
              background: `linear-gradient(135deg, #fef9c3, #fef08a)`,
              color: '#854d0e',
            }}
          >
            <span>{'\u26A0'}</span>
            <span>{deviationMessage}</span>
          </div>
        )}

        <div className="flex-1 flex flex-col gap-1 p-4">
          <h3 className="font-medium truncate" style={{ fontSize: 'clamp(0.75rem, 1.3vw, 1rem)', color: '#475569' }}>
            {title}
          </h3>

          <div className="mt-3">
            {isInitialLoading ? (
              <div className="h-10 w-28 rounded animate-pulse" style={{ backgroundColor: '#DDE2F0' }} />
            ) : (
              <div className="flex gap-2 items-baseline" style={{ color: activeColor }}>
              <span style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                fontWeight: 600,
                letterSpacing: '-0.02em',
              }}>
                  {hasData ? datum.toLocaleString(undefined, { maximumFractionDigits: 1 }) : 'N/A'}
                </span>
                {suffix && hasData && (
                  <span className="truncate" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 1rem)', color: '#475569' }}>
                    {formatUnit(suffix)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {expectedRange && (
          <div className="px-4 pb-3">
            <div className="pt-2" style={{ borderTop: '1px solid #DDE2F0' }}>
              {isInitialLoading ? (
                <div className="h-3 w-36 rounded animate-pulse" style={{ backgroundColor: '#DDE2F0' }} />
              ) : hasData ? (
                <span style={{ fontSize: 'clamp(0.65rem, 0.9vw, 0.75rem)', color: '#475569' }}>
                  Expected: {expectedRange.min.toLocaleString(undefined, { maximumFractionDigits: 0 })}{'\u2013'}{expectedRange.max.toLocaleString(undefined, { maximumFractionDigits: 0 })} {formatUnit(expectedRange.unit)}
                </span>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type PeriodAnalysisMetric = 'duration' | 'energy' | 'sensor_analysis';

interface PeriodAnalysisDatumConfig {
  title: string;
  metric: PeriodAnalysisMetric;
  sensorName?: string;
  targetUnit?: string;
  seriesId?: SeriesId;
  aggregateKey?: 'total' | 'average' | 'median' | 'minimum' | 'maximum';
  accentColor: string;
}

const PeriodAnalysisDatumCard = ({
  selectedAsset,
  config,
  data,
  isLoading
}: {
  selectedAsset: InstallationData;
  config: PeriodAnalysisDatumConfig;
  data: PeriodAnalysisData | undefined;
  isLoading: boolean;
}) => {
  const aggregateKey = config.aggregateKey ?? 'median';
  const series = config.seriesId
    ? data?.series.find(s => s.id === config.seriesId)
    : data?.series[0];
  const datum = series?.aggregates[aggregateKey] ?? undefined;
  const suffix = data?.units ?? undefined;

  let expectedRange: { min: number; max: number; unit: string } | undefined;
  let deviationMessage: string | undefined;

  if (data && config.metric === 'sensor_analysis') {
    const predictedMinSeries = data.series.find(s => s.id === 'predicted_range_min');
    const predictedMaxSeries = data.series.find(s => s.id === 'predicted_range_max');
    const minMedian = predictedMinSeries?.aggregates.median;
    const maxMedian = predictedMaxSeries?.aggregates.median;

    if (minMedian !== undefined && minMedian !== null && maxMedian !== undefined && maxMedian !== null) {
      expectedRange = {
        min: minMedian,
        max: maxMedian,
        unit: data.units || ''
      };

      if (datum !== undefined) {
        const title = config.title.toLowerCase();
        let deviationType: 'inlet' | 'outlet' | null = null;

        if (title.includes('compressor inlet')) {
          deviationType = 'inlet';
        } else if (title.includes('compressor outlet')) {
          deviationType = 'outlet';
        }

        if (deviationType) {
          const refrigerantType = selectedAsset.equipment_model?.refrigerant_type;
          const deviation = calculateDeviation(datum, minMedian, maxMedian, deviationType, refrigerantType);
          if (deviation) {
            deviationMessage = deviation.message;
          }
        }
      }
    }
  }

  return (
    <div className="flex flex-1 min-w-0">
      <PeriodAnalysisSnapshotCard
        title={config.title}
        datum={datum}
        suffix={suffix}
        isLoading={isLoading}
        expectedRange={expectedRange}
        deviationMessage={deviationMessage}
        accentColor={config.accentColor}
      />
    </div>
  );
};

const PERIOD_ANALYSIS_CONFIGS: PeriodAnalysisDatumConfig[] = [
  {
    title: "Compressor inlet",
    metric: 'sensor_analysis',
    sensorName: "Compressor inlet",
    targetUnit: 'degF',
    seriesId: 'measured_median',
    aggregateKey: 'median',
    accentColor: '#000F89',
  },
  {
    title: "Compressor outlet",
    metric: 'sensor_analysis',
    sensorName: "Compressor outlet",
    targetUnit: 'degF',
    seriesId: 'measured_median',
    aggregateKey: 'median',
    accentColor: '#000F89',
  },
  {
    title: "Total energy",
    metric: 'energy',
    aggregateKey: 'total',
    accentColor: '#000F89',
  },
  {
    title: "Average cycle duration",
    metric: 'duration',
    aggregateKey: 'average',
    accentColor: '#000F89',
  },
];

const COMPRESSOR_STATE_MODES = [
  'COMPRESSOR_WORKING',
  'DEFROST',
  'UNKNOWN_CURRENT_AND_COMPRESSOR',
];

export const HealthSnapshotSection = ({
  selectedAsset
}: {
  selectedAsset: InstallationData;
}) => {
  const [period, setPeriod] = useState<HealthSnapshotPeriod>(HEALTH_SNAPSHOT_PERIODS.LAST_7_DAYS);
  const periodConfig = HEALTH_SNAPSHOT_PERIOD_CONFIG[period];

  const requests = React.useMemo(() => {
    const now = new Date();
    const startTime = subHours(now, periodConfig.hours);

    return PERIOD_ANALYSIS_CONFIGS.map(config => ({
      installation_id: selectedAsset.id,
      start: startTime.toISOString(),
      end: now.toISOString(),
      metric: config.metric,
      state_mode: COMPRESSOR_STATE_MODES,
      ...(config.sensorName && { sensor_name: config.sensorName }),
      ...(config.targetUnit && { target_unit: config.targetUnit }),
    }));
  }, [selectedAsset.id, periodConfig.hours]);

  const { results, hasAnyCycles, isAnyFetching } = useMultiplePeriodAnalysis(requests);

  return (
    <div className="col-span-full w-full min-w-0">
      <Card className="p-3 lg:p-4 flex flex-col gap-4 overflow-hidden">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-base" style={{ color: '#1E293B' }}>Health snapshot</h2>
            <div className="relative group">
              <Info size={16} className="cursor-help" style={{ color: '#475569' }} />
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 p-2.5 rounded-lg bg-white text-xs leading-relaxed hidden group-hover:block z-50"
                   style={{ color: '#475569', boxShadow: '0 4px 12px rgba(0,15,137,0.12)' }}>
                Stats shown are for compressor-active cycles only. Fan-only and idle periods are excluded.
              </div>
            </div>
          </div>
          <div className="flex gap-0.5 p-1 rounded-full" style={{ backgroundColor: '#EEF0F8' }}>
            <button
              onClick={() => setPeriod(HEALTH_SNAPSHOT_PERIODS.LAST_24_HOURS)}
              className="h-8 px-4 text-xs font-medium rounded-full transition-all duration-150"
              style={period === HEALTH_SNAPSHOT_PERIODS.LAST_24_HOURS ? {
                backgroundColor: 'white',
                color: '#1E293B',
                boxShadow: '0 1px 3px rgba(0,15,137,0.14)',
              } : {
                backgroundColor: 'transparent',
                color: '#475569',
              }}
            >
              Last 24 hours
            </button>
            <button
              onClick={() => setPeriod(HEALTH_SNAPSHOT_PERIODS.LAST_7_DAYS)}
              className="h-8 px-4 text-xs font-medium rounded-full transition-all duration-150"
              style={period === HEALTH_SNAPSHOT_PERIODS.LAST_7_DAYS ? {
                backgroundColor: 'white',
                color: '#1E293B',
                boxShadow: '0 1px 3px rgba(0,15,137,0.14)',
              } : {
                backgroundColor: 'transparent',
                color: '#475569',
              }}
            >
              Last 7 days
            </button>
          </div>
        </div>
        {isAnyFetching ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
            {PERIOD_ANALYSIS_CONFIGS.map((config) => (
              <PeriodAnalysisDatumCard
                key={config.title}
                selectedAsset={selectedAsset}
                config={config}
                data={undefined}
                isLoading={true}
              />
            ))}
          </div>
        ) : !hasAnyCycles ? (
          <Card className="p-6 text-center border-2 border-dashed flex flex-col items-center gap-2">
            <p className="text-gray-600">We didn't detect any cycles for <span className="font-semibold">{selectedAsset.name}</span> during <span className="font-semibold">{periodConfig.display}</span>.</p>
            <p className="text-gray-600">If you recently switched the equipment on, it might take about an hour for us to start detecting cycles.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => results.forEach(result => result.refetch())}
              className="flex gap-1"
            >
              <RefreshCwIcon size={14}/>
              Refresh
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
            {PERIOD_ANALYSIS_CONFIGS.map((config, index) => (
              <PeriodAnalysisDatumCard
                key={config.title}
                selectedAsset={selectedAsset}
                config={config}
                data={results[index].data?.data}
                isLoading={results[index].isFetching}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
